import React, {
  PointerEvent as ReactPointerEvent,
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  AnyClip, AudioClip, ClipFraming, ClipSelection, ProjectAsset, TextClip,
  Timeline as TimelineType, TrackKind, VideoClip,
} from '../../types/editor';
import ClipContextMenu from './ClipContextMenu';
import { getCurrentAssetDrag } from './dragState';

/* After a successful asset-drop, the browser fires the trailing
 * pointer events on whichever element is under the cursor — which is
 * usually the track that just received the new clip. That would
 * immediately re-open the AddVideo/AddAudio modal. Suppress the track
 * click handler for a short window after a drop. */
const POST_DROP_CLICK_SUPPRESS_MS = 200;

const LONG_PRESS_MS = 350;
const MOVE_TO_DRAG_PX = 8;
const SNAP_TOLERANCE_PX = 6;
const MIN_CLIP_DURATION = 0.2;
const MIN_VISIBLE_RANGE_SEC = 1;

type GestureMode = 'drag' | 'trim-left' | 'trim-right';

interface Gesture {
  pointerId: number;
  mode: GestureMode;
  track: TrackKind;
  clipId: string;
  startX: number;
  /** Capture y too so the long-press → context menu can open at the
   *  original touch position even after the finger has moved slightly. */
  startY: number;
  startClipStart: number;
  startTrimIn: number;
  startTrimOut: number;
  armed: boolean;
  moved: boolean;
  timer: ReturnType<typeof setTimeout> | null;
}

interface PinchState {
  startDist: number;
  startPxPerSec: number;
}

/* Range-scrollbar drag state. Tracks which handle is being grabbed
 * plus the snapshot of (visible_start, visible_end) when the drag
 * began — applying deltas to the snapshot avoids drift from rounding
 * during the gesture. */
type RangeHandle = 'left' | 'right' | 'middle';
interface RangeDrag {
  pointerId: number;
  handle: RangeHandle;
  startX: number;
  startVisStart: number;
  startVisEnd: number;
  /* px-per-second of the timeline rail at gesture start; we keep
   * scroller's clientWidth as the anchor when re-deriving pxPerSec. */
  scrollerInnerWidth: number;
}

function clipDur(c: AnyClip): number { return c.trimOut - c.trimIn; }

function snap(value: number, snapPoints: number[], tolerancePx: number, pxPerSec: number): number {
  const tolSec = tolerancePx / pxPerSec;
  let best = value;
  let bestDist = tolSec;
  for (const p of snapPoints) {
    const d = Math.abs(value - p);
    if (d < bestDist) { best = p; bestDist = d; }
  }
  return best;
}

/* Pick a "nice" label interval in seconds based on pxPerSec so that
 * labels are roughly TARGET_LABEL_PX apart. Returns [labelInterval,
 * minorInterval] — 5 minor ticks per label. */
function tickIntervals(pxPerSec: number): [number, number] {
  const TARGET_LABEL_PX = 90;
  const targetSec = TARGET_LABEL_PX / pxPerSec;
  const nice = [0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
  const labelInterval = nice.find((v) => v >= targetSec) ?? 600;
  return [labelInterval, labelInterval / 5];
}

function fmtRuler(t: number): string {
  if (!isFinite(t) || t < 0) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t - m * 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

interface TimelineProps {
  timeline: TimelineType;
  assets: ProjectAsset[];
  pxPerSec: number;
  playhead: number;
  selectedClip: ClipSelection | null;
  /** Bumping this triggers an auto-fit (zoom out so all clips fit).
   *  EditorPage passes the project id so loading a new project always
   *  starts zoomed out. */
  autoFitKey?: number | string;
  onMoveClip: (track: TrackKind, clipId: string, patch: Partial<AnyClip>) => void;
  onTrimClip: (track: TrackKind, clipId: string, patch: Partial<AnyClip>) => void;
  onSelectClip: (selection: ClipSelection | null) => void;
  /** Right-click menu actions. Optional so read-only Timelines (if we
   *  ever have any) still work without them. */
  onDeleteClip?: (track: TrackKind, clipId: string) => void;
  onSplitClip?: (track: TrackKind, clipId: string) => void;
  /** Replace the per-aspect framing for a clip on the active aspect.
   *  Used by the Fill Width / Fill Height entries in the context
   *  menu. Routed through EditorPage so the aspect is implicit. */
  onSetFraming?: (clipId: string, framing: ClipFraming) => void;
  onSeek: (t: number) => void;
  onSetPxPerSec: (px: number) => void;
  /** Empty audio-track click when no audio clip exists yet — let the
   *  parent prompt the user to pick/upload an audio asset and drop it
   *  here. If undefined, the audio track behaves like the video track
   *  (click seeks). */
  onAddAudioAt?: (time: number) => void;
  /** Empty video-track click — let the parent prompt the user to
   *  pick / upload an image or video asset and drop it at the
   *  clicked time. If undefined, video-track clicks just seek. */
  onAddVideoAt?: (time: number) => void;
  /** Empty text-track click — create a text overlay clip at the
   *  clicked time. If undefined, clicks just seek. */
  onAddTextAt?: (time: number) => void;
  /** Header element (from the collapsible section's actions slot) that
   *  hosts the drag-to-create icons via portal — keeps the drag logic
   *  inside Timeline while the buttons sit on the section's title row. */
  toolbarHost?: HTMLElement | null;
  /** Native HTML5 drag-and-drop of a project asset onto the
   *  timeline. The Timeline picks the time from the drop x; the
   *  parent looks up the asset by id and dispatches ADD_CLIP. The
   *  reducer routes audio assets to the audio track and image /
   *  video assets to the video track regardless of which track the
   *  user dropped onto, so we don't need a track param here. */
  onDropAsset?: (time: number, assetId: number) => void;
}

export default function Timeline({
  timeline, assets, pxPerSec, playhead, selectedClip, autoFitKey,
  onMoveClip, onTrimClip, onSelectClip, onDeleteClip, onSplitClip,
  onSetFraming, onSeek, onSetPxPerSec,
  onAddAudioAt, onAddVideoAt, onAddTextAt, toolbarHost, onDropAsset,
}: TimelineProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const rulerScrubRef = useRef<number | null>(null); // pointerId currently scrubbing
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<PinchState | null>(null);
  const rangeRef = useRef<HTMLDivElement | null>(null);
  const rangeDragRef = useRef<RangeDrag | null>(null);

  /* Mirror the scroller's scrollLeft into React state so the range
   * scrollbar can render the visible-window highlight. We update on
   * every scroll event but the value is only used by the range bar's
   * paint, so the cost is bounded. */
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollerInnerWidth, setScrollerInnerWidth] = useState(600);

  /* Live drop indicator while a project-asset drag is hovering the
   * tracks. Stores the timeline time the cursor maps to so we can
   * render a vertical line at `time * pxPerSec`. null = no drag in
   * progress. */
  const [dropTime, setDropTime] = useState<number | null>(null);

  /* Timestamp (perf.now) of the last successful asset drop. Used to
   * suppress the empty-track-click handlers for a short window so
   * the trailing pointer release doesn't immediately re-open the
   * Add-Video/Add-Audio modal under the user's cursor. */
  const lastDropAtRef = useRef(0);

  /* New-clip drag from the toolbar icons (T / video / audio). Pointer
   * events (not HTML5 DnD) so it works on touch. While the pointer is
   * over the tracks a ghost clip previews the landing spot; releasing
   * inside creates (text directly; video/audio open their pickers at
   * that time), releasing outside cancels. */
  type NewClipKind = 'text' | 'video' | 'audio';
  interface NewClipDrag { kind: NewClipKind; time: number; inside: boolean }
  const [newClipDrag, setNewClipDrag] = useState<NewClipDrag | null>(null);
  const newClipDragRef = useRef<NewClipDrag | null>(null);

  const updateNewClipDrag = useCallback((d: NewClipDrag | null) => {
    newClipDragRef.current = d;
    setNewClipDrag(d);
  }, []);

  const startNewClipDrag = (kind: NewClipKind) => (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0 && e.pointerType !== 'touch') return;
    e.preventDefault();
    e.stopPropagation();
    try { (e.target as Element).setPointerCapture(e.pointerId); } catch (_) { /* OK */ }
    updateNewClipDrag({ kind, time: 0, inside: false });
  };

  const onNewClipDragMove = useCallback((e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = newClipDragRef.current;
    if (!d) return;
    const sc = scrollerRef.current;
    if (!sc) return;
    const r = sc.getBoundingClientRect();
    const inside = e.clientX >= r.left && e.clientX <= r.right
      && e.clientY >= r.top && e.clientY <= r.bottom;
    const sx = e.clientX - r.left + sc.scrollLeft;
    updateNewClipDrag({ ...d, inside, time: inside ? Math.max(0, sx / pxPerSec) : d.time });
  }, [pxPerSec, updateNewClipDrag]);

  const onNewClipDragEnd = useCallback(() => {
    const d = newClipDragRef.current;
    updateNewClipDrag(null);
    if (!d || !d.inside) return; // released outside the tracks → cancel
    if (d.kind === 'text') onAddTextAt?.(d.time);
    else if (d.kind === 'video') onAddVideoAt?.(d.time);
    else if (d.kind === 'audio' && timeline.audio.clips.length === 0) onAddAudioAt?.(d.time);
  }, [onAddTextAt, onAddVideoAt, onAddAudioAt, timeline.audio.clips.length, updateNewClipDrag]);

  /* Right-click context menu state. null = no menu open. */
  const [ctxMenu, setCtxMenu] = useState<
    { x: number; y: number; track: TrackKind; clipId: string } | null
  >(null);

  const assetById = useMemo(() => {
    const m = new Map<number, ProjectAsset>();
    for (const a of assets || []) m.set(a.id, a);
    return m;
  }, [assets]);

  /* Project duration = where actual content ends; the timeline view
   * always shows a small constant trailing zone past that so the
   * user can click / drag a clip into the empty area to extend the
   * project. Matches the convention in Premiere / Resolve /
   * Audacity (visible extension area), but with a flat 5s buffer
   * across all project lengths so the layout doesn't grow with
   * content. Empty projects show a 30s minimum so a brand-new
   * timeline is clickable. */
  const projectDuration = timeline.project.duration || 0;
  const TRAILING_EXTRA_SEC = 5;
  const MIN_DISPLAY_SEC = 30;
  const displayDuration = Math.max(
    projectDuration + TRAILING_EXTRA_SEC,
    MIN_DISPLAY_SEC,
  );
  /* `totalDuration` remains the name everything downstream uses
   * (snap, ruler, range scrollbar, auto-fit math) — it's just now
   * the DISPLAY duration, not the content duration. */
  const totalDuration = displayDuration;
  const contentEndPx = projectDuration * pxPerSec;
  const trackWidth = Math.max(totalDuration * pxPerSec, 600);

  /* Auto-fit: when the project changes, set pxPerSec so the whole
   * timeline fits inside the scroller. Stop responding once the user
   * touches a zoom control. */
  const didFitForRef = useRef<string | number | null>(null);
  useLayoutEffect(() => {
    if (autoFitKey == null) return;
    if (didFitForRef.current === autoFitKey) return;
    const sc = scrollerRef.current;
    if (!sc) return;
    const inner = sc.clientWidth || 600;
    setScrollerInnerWidth(inner);
    const dur = Math.max(timeline.project.duration || 0, 8);
    const fit = Math.max(2, Math.min(400, inner / dur));
    onSetPxPerSec(Math.floor(fit));
    didFitForRef.current = autoFitKey;
    sc.scrollLeft = 0;
    setScrollLeft(0);
    // Re-fit only when autoFitKey changes; ignore subsequent edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFitKey]);

  /* Keep the playhead in view by centering the scroller on it, with
   * the natural edge cases handled by clamping:
   *   - near t=0: clamp pins the view's left edge to the timeline
   *     start, so the playhead appears on the left.
   *   - near total duration: clamp pins the right edge, so the
   *     playhead appears on the right.
   *   - in between: scrollLeft = playheadX - viewport/2.
   * Skipped while the user is actively scrubbing the ruler or
   * dragging the range scrollbar so we don't fight their input. */
  useLayoutEffect(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    if (rulerScrubRef.current !== null) return;
    if (rangeDragRef.current) return;
    const playheadX = playhead * pxPerSec;
    const halfView = sc.clientWidth / 2;
    const maxScroll = Math.max(0, sc.scrollWidth - sc.clientWidth);
    const target = Math.max(0, Math.min(maxScroll, playheadX - halfView));
    // Only assign if it actually changes — avoids triggering an extra
    // onScroll → setScrollLeft cycle for noise-level deltas.
    if (Math.abs(sc.scrollLeft - target) > 0.5) {
      sc.scrollLeft = target;
    }
  }, [playhead, pxPerSec]);

  /* Keep scrollerInnerWidth fresh — needed by the range scrollbar so it
   * paints the right visible window after the viewport resizes. */
  useEffect(() => {
    const sc = scrollerRef.current;
    if (!sc) return undefined;
    const ro = new ResizeObserver(() => setScrollerInnerWidth(sc.clientWidth || 600));
    ro.observe(sc);
    return () => ro.disconnect();
  }, []);

  const snapPoints = useMemo(() => {
    const points: number[] = [0, playhead];
    for (const c of timeline.video.clips) {
      points.push(c.start);
      points.push(c.start + clipDur(c));
    }
    for (const c of timeline.audio.clips) {
      points.push(c.start);
      points.push(c.start + clipDur(c));
    }
    for (const c of timeline.overlay.clips) {
      points.push(c.start);
      points.push(c.start + (c.trimOut - c.trimIn));
    }
    return points;
  }, [timeline, playhead]);

  /* Map a clientX inside the ruler/track area to a timeline time. */
  const xToTime = useCallback((clientX: number): number => {
    const sc = scrollerRef.current;
    if (!sc) return 0;
    const rect = sc.getBoundingClientRect();
    const x = clientX - rect.left + sc.scrollLeft;
    return Math.max(0, x / pxPerSec);
  }, [pxPerSec]);

  // ---- ruler (click + drag to seek/scrub) ----
  const onRulerPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    try { (e.target as Element).setPointerCapture(e.pointerId); } catch (_) { /* OK */ }
    rulerScrubRef.current = e.pointerId;
    onSeek(xToTime(e.clientX));
  }, [onSeek, xToTime]);

  const onRulerPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (rulerScrubRef.current !== e.pointerId) return;
    onSeek(xToTime(e.clientX));
  }, [onSeek, xToTime]);

  const onRulerPointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (rulerScrubRef.current === e.pointerId) rulerScrubRef.current = null;
  }, []);

  // ---- track click ----
  /* Both tracks have their own variants below. The dedicated ruler
   * bar above the tracks is the canonical seek affordance now (it's
   * wider + more obvious), so the track's empty-area click is
   * repurposed to "add a clip here" — falling back to plain seek
   * when no add handler is wired. */

  /* Empty-track taps just seek. Clip creation moved to the toolbar's
   * drag-to-create icons — taps were too easy to trigger by accident. */
  const onTrackPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (performance.now() - lastDropAtRef.current < POST_DROP_CLICK_SUPPRESS_MS) return;
    onSeek(xToTime(e.clientX));
  }, [onSeek, xToTime]);

  /* HTML5 drop handlers for cross-component asset drags from
   * ProjectAssetsPanel. We read the active asset from a module-level
   * drag store (see dragState.ts) instead of dataTransfer.types
   * during dragover — some browsers strip custom MIMEs from the
   * types list at that stage, which would cause preventDefault to
   * be skipped and the drop silently canceled. The dataTransfer
   * payload is still set as a fallback for the actual drop. */
  const onTimelineDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!onDropAsset) return;
    if (!getCurrentAssetDrag()) return;
    e.preventDefault();
  }, [onDropAsset]);

  const onTimelineDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!onDropAsset) return;
    if (!getCurrentAssetDrag()) return;
    /* preventDefault here is what tells the browser "yes, accept the
     * drop here" — without it the drop event never fires. */
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTime(xToTime(e.clientX));
  }, [onDropAsset, xToTime]);

  const onTimelineDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Only clear if the cursor actually left the rail; React fires
    // dragleave on every internal hop too.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDropTime(null);
  }, []);

  const onTimelineDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!onDropAsset) return;
    e.preventDefault();
    /* Prefer the module-level drag store (set on dragstart, cleared
     * on dragend). Falls back to the custom MIME payload, then to
     * text/plain (which the panel also sets to the bare asset id),
     * so cross-window drags still work if they ever apply. */
    let id: number | null = null;
    const stored = getCurrentAssetDrag();
    if (stored) {
      id = stored.assetId;
    } else {
      const raw = e.dataTransfer.getData('application/x-megaphony-asset');
      if (raw) {
        try {
          const payload = JSON.parse(raw) as { id?: unknown };
          const parsed = Number(payload.id);
          if (Number.isFinite(parsed) && parsed > 0) id = parsed;
        } catch { /* fall through */ }
      }
      if (id === null) {
        const txt = e.dataTransfer.getData('text/plain');
        const parsed = Number(txt);
        if (Number.isFinite(parsed) && parsed > 0) id = parsed;
      }
    }
    setDropTime(null);
    if (id !== null) {
      lastDropAtRef.current = performance.now();
      onDropAsset(xToTime(e.clientX), id);
    }
  }, [onDropAsset, xToTime]);

  // ---- clip gesture (drag / trim / select) ----
  function startGesture(e: ReactPointerEvent, clip: AnyClip, track: TrackKind, mode: GestureMode): void {
    /* Reject anything that isn't the primary button — right-click
     * (button 2) and middle-click (button 1) shouldn't start a clip
     * gesture. The right-click case is handled by the clip's own
     * onContextMenu handler instead. e.button is 0 for touch too,
     * so this check works across pointer types. */
    if (e.button !== 0) return;
    e.stopPropagation();
    try { (e.target as Element).setPointerCapture(e.pointerId); } catch (_) { /* OK */ }
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 2) {
      const pts = Array.from(pointersRef.current.values());
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      pinchRef.current = { startDist: dist || 1, startPxPerSec: pxPerSec };
      gestureRef.current = null;
      return;
    }

    const isTouch = e.pointerType === 'touch';
    /* On touch, drag mode starts in a "long-press window": holding
     * still for LONG_PRESS_MS without moving opens the context menu;
     * moving during the window arms the drag immediately; releasing
     * is a tap (handled in onClipPointerUp). Trim handles skip this
     * — touching a 16px-wide handle is unambiguous. */
    const useLongPress = isTouch && mode === 'drag';
    const startX = e.clientX;
    const startY = e.clientY;
    gestureRef.current = {
      pointerId: e.pointerId,
      mode, track,
      clipId: clip.id,
      startX,
      startY,
      startClipStart: clip.start,
      startTrimIn: clip.trimIn,
      startTrimOut: clip.trimOut,
      armed: !useLongPress,
      moved: false,
      timer: useLongPress
        ? setTimeout(() => {
            const g = gestureRef.current;
            if (!g || g.moved) return;
            /* Long-press held → open context menu at the touch point
             * and clear the gesture so the trailing pointerup doesn't
             * also fire selectClip (which would open the inspector). */
            if (g.track !== 'overlay') {
              setCtxMenu({ x: g.startX, y: g.startY, track: g.track, clipId: g.clipId });
            }
            gestureRef.current = null;
          }, LONG_PRESS_MS)
        : null,
    };
  }

  function onClipPointerMove(e: ReactPointerEvent): void {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (pointersRef.current.size === 2 && pinchRef.current) {
      const pts = Array.from(pointersRef.current.values());
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      const ratio = dist / pinchRef.current.startDist;
      onSetPxPerSec(Math.round(pinchRef.current.startPxPerSec * ratio));
      didFitForRef.current = autoFitKey ?? null; // user-driven zoom = lock auto-fit
      return;
    }
    const g = gestureRef.current;
    if (!g || g.pointerId !== e.pointerId) return;

    const dx = e.clientX - g.startX;
    if (Math.abs(dx) > MOVE_TO_DRAG_PX) g.moved = true;
    if (!g.armed) {
      /* Touch moved during the long-press window → that's a drag, not
       * a long-press → cancel the menu timer and arm the gesture so
       * subsequent moves apply onMoveClip / onTrimClip normally. */
      if (g.timer && Math.abs(dx) > MOVE_TO_DRAG_PX) {
        clearTimeout(g.timer);
        g.timer = null;
        g.armed = true;
      } else {
        return;
      }
    }

    const dt = dx / pxPerSec;
    const list = timeline[g.track].clips as unknown as AnyClip[];
    const clip = list.find((c) => c.id === g.clipId);
    if (!clip) return;

    if (g.mode === 'drag') {
      const proposed = Math.max(0, g.startClipStart + dt);
      const candidates = snapPoints.filter((p) => Math.abs(p - proposed) < 1);
      const snapped = snap(proposed, snapPoints.filter((p) => candidates.indexOf(p) === -1), SNAP_TOLERANCE_PX, pxPerSec);
      onMoveClip(g.track, g.clipId, { start: snapped });
    } else if (g.mode === 'trim-left') {
      const newTrimIn = Math.max(0, g.startTrimIn + dt);
      const actualDt = newTrimIn - g.startTrimIn;
      const newStart = Math.max(0, g.startClipStart + actualDt);
      if (g.startTrimOut - newTrimIn < MIN_CLIP_DURATION) return;
      onTrimClip(g.track, g.clipId, { trimIn: newTrimIn, start: newStart });
    } else if (g.mode === 'trim-right') {
      const asset = assetById.get(clip.assetId);
      const isMediaWithDur = asset && asset.duration && asset.kind !== 'image';
      // Text overlays have no source media — allow generous durations.
      const maxTrimOut = g.track === 'overlay'
        ? 600
        : (isMediaWithDur ? (asset!.duration as number) : 30);
      const candidate = Math.max(g.startTrimIn + MIN_CLIP_DURATION, g.startTrimOut + dt);
      const newTrimOut = Math.min(maxTrimOut, candidate);
      onTrimClip(g.track, g.clipId, { trimOut: newTrimOut });
    }
  }

  function onClipPointerUp(e: ReactPointerEvent): void {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    const g = gestureRef.current;
    if (g && g.pointerId === e.pointerId) {
      if (g.timer) clearTimeout(g.timer);
      if (!g.moved) onSelectClip({ track: g.track, id: g.clipId });
      gestureRef.current = null;
    }
  }

  // ---- range scrollbar (zoom + pan) ----
  const visStart = pxPerSec > 0 ? scrollLeft / pxPerSec : 0;
  const visEnd = Math.min(totalDuration, visStart + scrollerInnerWidth / pxPerSec);

  function applyRange(newStart: number, newEnd: number): void {
    let s = Math.max(0, newStart);
    let e = Math.min(totalDuration, newEnd);
    if (e - s < MIN_VISIBLE_RANGE_SEC) {
      // Preserve the dragged handle's anchor by nudging the OTHER end.
      if (s !== visStart) s = Math.max(0, e - MIN_VISIBLE_RANGE_SEC);
      else e = Math.min(totalDuration, s + MIN_VISIBLE_RANGE_SEC);
    }
    const span = e - s;
    if (span <= 0) return;
    const newPxPerSec = Math.max(0.1, Math.min(400, scrollerInnerWidth / span));
    onSetPxPerSec(newPxPerSec);
    didFitForRef.current = autoFitKey ?? null;
    // The scroll-position update needs to happen AFTER pxPerSec lands
    // in the DOM (track width recomputes). Use rAF so the next paint
    // has both values in sync.
    requestAnimationFrame(() => {
      const sc = scrollerRef.current;
      if (!sc) return;
      sc.scrollLeft = s * newPxPerSec;
      setScrollLeft(sc.scrollLeft);
    });
  }

  const onRangePointerDown = useCallback((handle: RangeHandle) => (e: ReactPointerEvent) => {
    e.stopPropagation();
    try { (e.target as Element).setPointerCapture(e.pointerId); } catch (_) { /* OK */ }
    rangeDragRef.current = {
      pointerId: e.pointerId, handle,
      startX: e.clientX,
      startVisStart: visStart,
      startVisEnd: visEnd,
      scrollerInnerWidth,
    };
  }, [visStart, visEnd, scrollerInnerWidth]);

  const onRangePointerMove = useCallback((e: ReactPointerEvent) => {
    const d = rangeDragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const rangeEl = rangeRef.current;
    if (!rangeEl) return;
    const rangeWidth = rangeEl.clientWidth || 1;
    const secPerPx = totalDuration / rangeWidth;
    const dtSec = (e.clientX - d.startX) * secPerPx;
    if (d.handle === 'left') {
      applyRange(d.startVisStart + dtSec, d.startVisEnd);
    } else if (d.handle === 'right') {
      applyRange(d.startVisStart, d.startVisEnd + dtSec);
    } else {
      // Middle drag = pan. Clamp so the window stays inside [0, total].
      const span = d.startVisEnd - d.startVisStart;
      let newStart = d.startVisStart + dtSec;
      if (newStart < 0) newStart = 0;
      if (newStart + span > totalDuration) newStart = totalDuration - span;
      applyRange(newStart, newStart + span);
    }
  }, [totalDuration]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRangePointerUp = useCallback((e: ReactPointerEvent) => {
    const d = rangeDragRef.current;
    if (d && d.pointerId === e.pointerId) rangeDragRef.current = null;
  }, []);

  /* Map (visStart, visEnd) → range bar (left%, width%) for the
   * highlighted viewport. */
  const rangeViewportStyle: React.CSSProperties = {
    left: `${(visStart / Math.max(totalDuration, 0.001)) * 100}%`,
    width: `${Math.max(0.5, ((visEnd - visStart) / Math.max(totalDuration, 0.001)) * 100)}%`,
  };

  // ---- ruler ticks ----
  const [labelInterval, minorInterval] = tickIntervals(pxPerSec);
  const ticks = useMemo(() => {
    const out: Array<{ t: number; major: boolean }> = [];
    for (let t = 0; t <= totalDuration + 0.0001; t += minorInterval) {
      const rounded = Math.round(t / minorInterval) * minorInterval;
      out.push({ t: rounded, major: Math.abs(rounded % labelInterval) < 0.0001 });
    }
    return out;
  }, [totalDuration, labelInterval, minorInterval]);

  // ---- clip rendering ----
  function renderClip(clip: AnyClip, track: TrackKind): React.ReactElement {
    const width = Math.max(clipDur(clip) * pxPerSec, 24);
    const left = clip.start * pxPerSec;
    const isText = track === 'overlay';
    const asset = isText ? undefined : assetById.get(clip.assetId);
    const isSelected = selectedClip?.track === track && selectedClip?.id === clip.id;
    const label = isText
      ? ((clip as unknown as TextClip).text || 'Text')
      : (asset?.original_filename || `${track === 'audio' ? 'Audio' : asset?.kind || 'Clip'}`);
    const videoClip = track === 'video' ? (clip as VideoClip) : null;
    const xfade = videoClip && videoClip.transitionIn?.type === 'crossfade' && videoClip.transitionIn?.duration
      ? videoClip.transitionIn.duration * pxPerSec
      : 0;
    return (
      <div
        key={clip.id}
        className={`timeline-clip kind-${isText ? 'text' : (asset?.kind || 'video')} ${isSelected ? 'selected' : ''}`}
        style={{ width, left }}
        onPointerDown={(e) => startGesture(e, clip, track, 'drag')}
        onPointerMove={onClipPointerMove}
        onPointerUp={onClipPointerUp}
        onPointerCancel={onClipPointerUp}
        onContextMenu={(e) => {
          /* Right-click → custom menu. preventDefault stops the
           * browser's native menu from showing alongside ours. */
          e.preventDefault();
          e.stopPropagation();
          /* The context menu's entries (fill width/height, split) are
           * media-clip concepts; text clips edit via the inspector. */
          if (track !== 'overlay') {
            setCtxMenu({ x: e.clientX, y: e.clientY, track, clipId: clip.id });
          }
        }}
      >
        <div
          className="timeline-trim-handle left"
          onPointerDown={(e) => startGesture(e, clip, track, 'trim-left')}
        />
        {xfade > 0 && (
          <div className="timeline-xfade-marker" style={{ width: xfade }} aria-label="crossfade" />
        )}
        <span className="timeline-clip-label">{label}</span>
        <div
          className="timeline-trim-handle right"
          onPointerDown={(e) => startGesture(e, clip, track, 'trim-right')}
        />
      </div>
    );
  }

  const GHOST_DUR: Record<NewClipKind, number> = { text: 3, video: 5, audio: 10 };
  const ghostFor = (kind: NewClipKind): React.ReactElement | null => {
    if (!newClipDrag || !newClipDrag.inside || newClipDrag.kind !== kind) return null;
    return (
      <div
        className="timeline-ghost-clip"
        style={{
          left: newClipDrag.time * pxPerSec,
          width: Math.max(24, GHOST_DUR[kind] * pxPerSec),
        }}
        aria-hidden
      />
    );
  };

  const toolbar = (
    <span className="timeline-toolbar">
      <button
        type="button"
        className="timeline-new-btn"
        title="Drag onto the Text track to add a caption"
        aria-label="New text clip (drag onto the timeline)"
        onPointerDown={startNewClipDrag('text')}
        onPointerMove={onNewClipDragMove}
        onPointerUp={onNewClipDragEnd}
        onPointerCancel={onNewClipDragEnd}
      >T</button>
      <button
        type="button"
        className="timeline-new-btn"
        title="Drag onto the Video track to add an image / video clip"
        aria-label="New video clip (drag onto the timeline)"
        onPointerDown={startNewClipDrag('video')}
        onPointerMove={onNewClipDragMove}
        onPointerUp={onNewClipDragEnd}
        onPointerCancel={onNewClipDragEnd}
      >🎬</button>
      <button
        type="button"
        className="timeline-new-btn"
        title={timeline.audio.clips.length > 0
          ? 'Audio track already has a clip'
          : 'Drag onto the Audio track to add music'}
        aria-label="New audio clip (drag onto the timeline)"
        disabled={timeline.audio.clips.length > 0}
        onPointerDown={startNewClipDrag('audio')}
        onPointerMove={onNewClipDragMove}
        onPointerUp={onNewClipDragEnd}
        onPointerCancel={onNewClipDragEnd}
      >♪</button>
    </span>
  );

  return (
    <div className="timeline-wrap">
      {toolbarHost ? createPortal(toolbar, toolbarHost) : toolbar}
      <div
        ref={scrollerRef}
        className="timeline-scroller"
        onScroll={() => {
          const sc = scrollerRef.current;
          if (sc) setScrollLeft(sc.scrollLeft);
        }}
        onWheel={(e) => {
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const factor = e.deltaY > 0 ? 0.9 : 1.1;
            onSetPxPerSec(Math.round(pxPerSec * factor));
            didFitForRef.current = autoFitKey ?? null;
          }
        }}
      >
        <div
          className="timeline-rail"
          style={{ width: trackWidth }}
          onDragEnter={onTimelineDragEnter}
          onDragOver={onTimelineDragOver}
          onDragLeave={onTimelineDragLeave}
          onDrop={onTimelineDrop}
        >
          {/* Time ruler: ticks every minor interval; labels at majors.
              Click + drag scrubs the playhead. Lives inside the same
              scroller as the tracks so it always lines up with them. */}
          <div
            className="timeline-ruler"
            onPointerDown={onRulerPointerDown}
            onPointerMove={onRulerPointerMove}
            onPointerUp={onRulerPointerUp}
            onPointerCancel={onRulerPointerUp}
          >
            {ticks.map((tk) => (
              <div
                key={tk.t}
                className={`timeline-ruler-tick${tk.major ? ' is-major' : ''}`}
                style={{ left: tk.t * pxPerSec }}
              >
                {tk.major && <span className="timeline-ruler-label">{fmtRuler(tk.t)}</span>}
              </div>
            ))}
            <div
              className="timeline-ruler-playhead"
              style={{ left: playhead * pxPerSec }}
              aria-hidden
            />
          </div>

          <div className="timeline-tracks">
            <div className="timeline-track overlay" onPointerDown={onTrackPointerDown}>
              <div className="timeline-track-label">Text</div>
              {projectDuration > 0 && contentEndPx < trackWidth && (
                <div
                  className="timeline-track-trailing"
                  style={{ left: contentEndPx }}
                  aria-hidden
                />
              )}
              {ghostFor('text')}
              {(timeline.overlay.clips as unknown as AnyClip[]).map((c) => renderClip(c, 'overlay'))}
            </div>
            <div className="timeline-track video" onPointerDown={onTrackPointerDown}>
              <div className="timeline-track-label">Video</div>
              {projectDuration > 0 && contentEndPx < trackWidth && (
                <div
                  className="timeline-track-trailing"
                  style={{ left: contentEndPx }}
                  aria-hidden
                />
              )}
              {ghostFor('video')}
              {(timeline.video.clips as VideoClip[]).map((c) => renderClip(c, 'video'))}
            </div>
            <div className="timeline-track audio" onPointerDown={onTrackPointerDown}>
              <div className="timeline-track-label">Audio</div>
              {projectDuration > 0 && contentEndPx < trackWidth && (
                <div
                  className="timeline-track-trailing"
                  style={{ left: contentEndPx }}
                  aria-hidden
                />
              )}
              {ghostFor('audio')}
              {(timeline.audio.clips as AudioClip[]).map((c) => renderClip(c, 'audio'))}
            </div>
            <div
              className="timeline-playhead"
              style={{ left: playhead * pxPerSec }}
              aria-hidden
            />
            {/* Drop cursor — only visible while a project-asset drag
                is hovering the rail. Paints a vertical accent line at
                the cursor's timeline position so the user sees
                exactly where the dropped clip will land. */}
            {dropTime !== null && (
              <div
                className="timeline-drop-cursor"
                style={{ left: dropTime * pxPerSec }}
                aria-hidden
              />
            )}
          </div>
        </div>
      </div>

      {/* Range scrollbar: zoom + pan. The viewport rectangle in the
          middle represents the currently-visible window. Drag its
          left or right edge to change the zoom; drag the body to pan. */}
      <div
        className="timeline-range"
        ref={rangeRef}
        onPointerMove={onRangePointerMove}
        onPointerUp={onRangePointerUp}
        onPointerCancel={onRangePointerUp}
      >
        <div
          className="timeline-range-viewport"
          style={rangeViewportStyle}
          onPointerDown={onRangePointerDown('middle')}
        >
          <div
            className="timeline-range-handle left"
            onPointerDown={onRangePointerDown('left')}
            aria-label="Adjust visible start"
          />
          <div
            className="timeline-range-handle right"
            onPointerDown={onRangePointerDown('right')}
            aria-label="Adjust visible end"
          />
        </div>
      </div>

      {ctxMenu && (() => {
        const list = timeline[ctxMenu.track].clips as unknown as AnyClip[];
        const clip = list.find((c) => c.id === ctxMenu.clipId);
        if (!clip) return null;
        const asset = assetById.get(clip.assetId);
        return (
          <ClipContextMenu
            x={ctxMenu.x}
            y={ctxMenu.y}
            track={ctxMenu.track}
            clip={clip}
            trackClips={list}
            asset={asset}
            canvasW={timeline.project.width}
            canvasH={timeline.project.height}
            onClose={() => setCtxMenu(null)}
            onMoveClip={(patch) => onMoveClip(ctxMenu.track, ctxMenu.clipId, patch)}
            onTrimClip={(patch) => onTrimClip(ctxMenu.track, ctxMenu.clipId, patch)}
            onSplitClip={() => onSplitClip?.(ctxMenu.track, ctxMenu.clipId)}
            onDeleteClip={() => onDeleteClip?.(ctxMenu.track, ctxMenu.clipId)}
            onSetFraming={(framing) => onSetFraming?.(ctxMenu.clipId, framing)}
          />
        );
      })()}
    </div>
  );
}
